import {
	type SnapMove,
	type SnapState,
	speedSnap,
	type WarMove,
	type WarState,
	warOfCards,
} from "../games/cards";
import {
	CHARADE_EMOJI,
	type CharadesMove,
	type CharadesState,
	emojiCharades,
} from "../games/charades";
import { type CheckersMove, type CheckersState, captureCount, checkers } from "../games/checkers";
import {
	CODE_COLOURS,
	CODE_LENGTH,
	type CodeBreakerMove,
	type CodeBreakerState,
	codeBreaker,
	consistentCodes,
} from "../games/codebreaker";
import { C4_COLS, type Connect4Move, type Connect4State, connect4 } from "../games/connect4";
import {
	DB_BOXES,
	DB_DOTS,
	type DotsBoxesMove,
	type DotsBoxesState,
	dotsBoxes,
} from "../games/dotsboxes";
import { type DotsRaceMove, type DotsRaceState, dotsRace } from "../games/dotsrace";
import { FIR_SIZE, type FiveInRowMove, type FiveInRowState, fiveInRow } from "../games/fiveinrow";
import { FLEET_SIZE, type FleetMove, type FleetState, fleetBattle } from "../games/fleet";
import { type FlipFourMove, type FlipFourState, flipFour, flipsFor } from "../games/flipfour";
import type { ChoiceDuelState, ChoiceMove } from "../games/framework/choiceDuel";
import {
	type GoFishMove,
	type GoFishState,
	goFish,
	type OldMaidMove,
	type OldMaidState,
	oldMaid,
} from "../games/handgames";
import { DRAW_TWO, type LastCardMove, type LastCardState, lastCard, SKIP } from "../games/lastcard";
import { deduceGrid, type LogicGridMove, type LogicGridState, logicGrid } from "../games/logicgrid";
import { type MemoryMove, type MemoryState, memory } from "../games/memory";
import {
	MINE_SIZE,
	type MinesweeperMove,
	type MinesweeperState,
	minesweeperDuel,
} from "../games/minesweeper";
import { emojiMosaic, MOSAIC_TILES, type MosaicMove, type MosaicState } from "../games/mosaic";
import { type NimMove, type NimState, nim, nimBestTake } from "../games/nim";
import { type NumberGuessMove, type NumberGuessState, numberGuess } from "../games/numberguess";
import { bestPegMove, type PegChaseMove, type PegChaseState, pegChase } from "../games/pegchase";
import { type PitRowMove, type PitRowState, pitRow } from "../games/pitrow";
import type { QuizMove, QuizState } from "../games/quiz";
import { type ReflexMove, type ReflexState, reflexTap } from "../games/reflex";
import {
	bestBlockerMove,
	type RouteBlockerMove,
	type RouteBlockerState,
	routeBlocker,
} from "../games/routeblocker";
import { type RpsMove, type RpsState, rockPaperScissors } from "../games/rps";
import { bestRummyMove, type RummyMove, type RummyState, rummyRush } from "../games/rummy";
import { type SlidingMove, type SlidingState, slidingRace } from "../games/sliding";
import { DASH_WINDOW_MS, type SnailDashMove, type SnailDashState } from "../games/snaildash";
import {
	bestLadderChoice,
	type SnakesLaddersMove,
	type SnakesLaddersState,
	snakesLadders,
} from "../games/snakesladders";
import { fits, type SuitRunMove, type SuitRunState, suitRun } from "../games/suitrun";
import { type TicTacToeMove, type TicTacToeState, ticTacToe } from "../games/tictactoe";
import {
	TOWER_CLAIM_HEIGHT,
	type TowerSwapMove,
	type TowerSwapState,
	towerSwap,
} from "../games/towerswap";
import {
	bestGear,
	cornersCrossed,
	GEARS,
	TRACK_LENGTH,
	type TurboTrackMove,
	type TurboTrackState,
	turboTrack,
} from "../games/turbotrack";
import { type TypingLadderMove, type TypingLadderState, typingLadder } from "../games/typingladder";
import {
	bestLadderStep,
	type WordLadderMove,
	type WordLadderState,
	wordLadder,
} from "../games/wordladder";
import type { GameState } from "../registry";
import { createRng, type Rng } from "../rng";
import { otherPlayer, type PlayerIndex } from "../types";

export const DIFFICULTIES = ["easy", "medium", "hard"] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

export function isDifficulty(value: unknown): value is Difficulty {
	return typeof value === "string" && (DIFFICULTIES as readonly string[]).includes(value);
}

export const DIFFICULTY_LABEL: Record<Difficulty, string> = {
	easy: "Easy — makes mistakes",
	medium: "Medium — pays attention",
	hard: "Hard — plays properly",
};

/** How long the computer "thinks" before moving, so it doesn't feel robotic. */
export const BOT_THINK_MS: Record<Difficulty, [number, number]> = {
	easy: [700, 1400],
	medium: [600, 1300],
	hard: [500, 1200],
};

export function botThinkDelay(difficulty: Difficulty, rng: Rng): number {
	const [min, max] = BOT_THINK_MS[difficulty];
	return min + rng.int(max - min);
}

/** How often the bot plays its best move rather than something random. */
const SHARPNESS: Record<Difficulty, number> = { easy: 0.25, medium: 0.65, hard: 1 };

/** Memory is different: the bot can see the deck, so this is what it recalls. */
const MEMORY_RECALL: Record<Difficulty, number> = { easy: 0.25, medium: 0.6, hard: 0.9 };

/**
 * Picks a move for the computer. Returns `null` when there is nothing to play —
 * it isn't the bot's turn, or the game is over.
 *
 * Every policy is pure and seeded, so a bot's behaviour is reproducible in a
 * test rather than something you have to watch to believe.
 */
export function botMove(
	state: GameState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): unknown | null {
	switch (state.kind) {
		case "tictactoe":
			return ticTacToeBot(state, seat, difficulty, rng);
		case "connect4":
			return connect4Bot(state, seat, difficulty, rng);
		case "memory":
			return memoryBot(state, seat, difficulty, rng);
		case "dotsboxes":
			return dotsBoxesBot(state, seat, difficulty, rng);
		case "quiz":
			return quizBot(state, seat, difficulty, rng);
		case "nim":
			return nimBot(state, seat, difficulty, rng);
		case "fiveinrow":
			return fiveInRowBot(state, seat, difficulty, rng);
		case "rps":
			return rpsBot(state, seat, rng);
		case "reflex":
			return reflexBot(state, seat, difficulty, rng);
		case "war":
			return warBot(state, seat);
		case "snap":
			return snapBot(state, seat, difficulty, rng);
		case "numberguess":
			return numberGuessBot(state, seat, difficulty, rng);
		case "mosaic":
			return mosaicBot(state, seat, difficulty, rng);
		case "fleet":
			return fleetBot(state, seat, difficulty, rng);
		case "flipfour":
			return flipFourBot(state, seat, difficulty, rng);
		case "codebreaker":
			return codeBreakerBot(state, seat, difficulty, rng);
		case "sliding":
			return slidingBot(state, seat, difficulty, rng);
		case "pitrow":
			return pitRowBot(state, seat, difficulty, rng);
		case "checkers":
			return checkersBot(state, seat, difficulty, rng);
		case "gofish":
			return goFishBot(state, seat, difficulty, rng);
		case "oldmaid":
			return oldMaidBot(state, seat, rng);
		case "minesweeper":
			return minesweeperBot(state, seat, difficulty, rng);
		case "lastcard":
			return lastCardBot(state, seat, difficulty, rng);
		case "dotsrace":
			return dotsRaceBot(state, seat, difficulty, rng);
		case "typingladder":
			return typingLadderBot(state, seat, difficulty, rng);
		case "charades":
			return charadesBot(state, seat, difficulty, rng);
		case "suitrun":
			return suitRunBot(state, seat, difficulty, rng);
		case "towerswap":
			return towerSwapBot(state, seat, difficulty, rng);
		case "wordladder":
			return wordLadderBot(state, seat, difficulty, rng);
		case "snaildash":
			return snailDashBot(state, seat, difficulty);
		case "routeblocker":
			return routeBlockerBot(state, seat, difficulty, rng);
		case "logicgrid":
			return logicGridBot(state, seat, difficulty, rng);
		case "rummy":
			return rummyBot(state, seat, difficulty, rng);
		case "pegchase":
			return pegChaseBot(state, seat, difficulty, rng);
		case "snakesladders":
			return snakesLaddersBot(state, seat, difficulty, rng);
		case "turbotrack":
			return turboTrackBot(state, seat, difficulty, rng);
		default:
			return choiceDuelBot(state, seat, difficulty, rng);
	}
}

// --- Choice duels: knows every answer, told how often to get one right. ---

function choiceDuelBot(
	state: ChoiceDuelState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): ChoiceMove | null {
	if (state.result || state.phase !== "question" || state.answers[seat]) return null;
	const round = state.rounds[state.index];
	if (!round || round.choices.length === 0) return null;

	// A round with no right answer (would-you-rather) is pure opinion: the bot
	// just has one, and agreeing with it is the player's job.
	if (round.answer < 0) return { choice: rng.int(round.choices.length) };

	if (rng.next() < QUIZ_ACCURACY[difficulty]) return { choice: round.answer };
	const wrong = round.choices
		.map((_, choice) => choice)
		.filter((choice) => choice !== round.answer);
	return { choice: wrong[rng.int(wrong.length)] ?? round.answer };
}

function maybeSharp(difficulty: Difficulty, rng: Rng): boolean {
	return rng.next() < SHARPNESS[difficulty];
}

function pick<T>(moves: T[], rng: Rng): T | null {
	return moves.length === 0 ? null : moves[rng.int(moves.length)];
}

// --- Tic-tac-toe: solved by minimax, so "hard" genuinely cannot be beaten. ---

function ticTacToeBot(
	state: TicTacToeState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): TicTacToeMove | null {
	const moves = ticTacToe.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	let best: TicTacToeMove | null = null;
	let bestScore = Number.NEGATIVE_INFINITY;
	for (const move of moves) {
		const outcome = ticTacToe.apply(state, seat, move, 0);
		if (!outcome.ok) continue;
		const score = -negamaxTicTacToe(outcome.state, otherPlayer(seat), 0);
		if (score > bestScore) {
			bestScore = score;
			best = move;
		}
	}
	return best ?? pick(moves, rng);
}

/** Positive is good for `player`. Shallower wins score higher, so it finishes. */
function negamaxTicTacToe(state: TicTacToeState, player: PlayerIndex, depth: number): number {
	if (state.result) {
		if (state.result.winner === null) return 0;
		return state.result.winner === player ? 10 - depth : depth - 10;
	}
	let best = Number.NEGATIVE_INFINITY;
	for (const move of ticTacToe.legalMoves(state, player)) {
		const outcome = ticTacToe.apply(state, player, move, 0);
		if (!outcome.ok) continue;
		best = Math.max(best, -negamaxTicTacToe(outcome.state, otherPlayer(player), depth + 1));
	}
	return best === Number.NEGATIVE_INFINITY ? 0 : best;
}

// --- Connect Four: negamax over a windowed heuristic. ---

const C4_DEPTH: Record<Difficulty, number> = { easy: 1, medium: 3, hard: 4 };

function connect4Bot(
	state: Connect4State,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): Connect4Move | null {
	const moves = connect4.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	// All the equally-best columns, decided between by the dice: a purely
	// deterministic pick made two hard bots replay one identical game on
	// every seed, with the same seat winning 30 times out of 30.
	let bestMoves: Connect4Move[] = [];
	let bestScore = Number.NEGATIVE_INFINITY;
	// Centre-first: better pruning, and a nicer-looking opening.
	const ordered = [...moves].sort((a, b) => Math.abs(3 - a.column) - Math.abs(3 - b.column));
	for (const move of ordered) {
		const outcome = connect4.apply(state, seat, move, 0);
		if (!outcome.ok) continue;
		const score = -negamaxConnect4(
			outcome.state,
			otherPlayer(seat),
			C4_DEPTH[difficulty],
			Number.NEGATIVE_INFINITY,
			Number.POSITIVE_INFINITY,
		);
		if (score > bestScore) {
			bestScore = score;
			bestMoves = [move];
		} else if (score === bestScore) {
			bestMoves.push(move);
		}
	}
	return pick(bestMoves, rng) ?? pick(moves, rng);
}

function negamaxConnect4(
	state: Connect4State,
	player: PlayerIndex,
	depth: number,
	alpha: number,
	beta: number,
): number {
	if (state.result) {
		if (state.result.winner === null) return 0;
		return state.result.winner === player ? 100_000 + depth : -100_000 - depth;
	}
	if (depth === 0) return scoreConnect4(state, player);

	let best = Number.NEGATIVE_INFINITY;
	let a = alpha;
	for (const move of connect4.legalMoves(state, player)) {
		const outcome = connect4.apply(state, player, move, 0);
		if (!outcome.ok) continue;
		const score = -negamaxConnect4(outcome.state, otherPlayer(player), depth - 1, -beta, -a);
		best = Math.max(best, score);
		a = Math.max(a, score);
		if (a >= beta) break;
	}
	return best === Number.NEGATIVE_INFINITY ? 0 : best;
}

/** Counts every four-in-a-row window and rewards the ones we're closer to filling. */
function scoreConnect4(state: Connect4State, player: PlayerIndex): number {
	const board = state.board;
	const at = (col: number, row: number) => board[row * C4_COLS + col];
	let score = 0;

	const windows: [number, number][][] = [];
	for (let row = 0; row < 6; row++) {
		for (let col = 0; col < C4_COLS; col++) {
			if (col + 3 < C4_COLS) windows.push([0, 1, 2, 3].map((i) => [col + i, row]));
			if (row + 3 < 6) windows.push([0, 1, 2, 3].map((i) => [col, row + i]));
			if (col + 3 < C4_COLS && row + 3 < 6)
				windows.push([0, 1, 2, 3].map((i) => [col + i, row + i]));
			if (col + 3 < C4_COLS && row - 3 >= 0)
				windows.push([0, 1, 2, 3].map((i) => [col + i, row - i]));
		}
	}

	for (const window of windows) {
		let mine = 0;
		let theirs = 0;
		for (const [col, row] of window) {
			const cell = at(col, row);
			if (cell === player) mine++;
			else if (cell !== null) theirs++;
		}
		if (mine > 0 && theirs > 0) continue;
		if (mine === 3) score += 50;
		else if (mine === 2) score += 8;
		else if (mine === 1) score += 1;
		if (theirs === 3) score -= 60;
		else if (theirs === 2) score -= 8;
		else if (theirs === 1) score -= 1;
	}
	return score;
}

// --- Memory: full knowledge, deliberately forgotten some of the time. ---

function memoryBot(
	state: MemoryState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): MemoryMove | null {
	const moves = memory.legalMoves(state, seat);
	if (moves.length === 0) return null;

	// The bot sees the whole deck, so "difficulty" is really "how much of what
	// it saw does it remember" — anything else would be a cheat, not an opponent.
	const remembers = rng.next() < MEMORY_RECALL[difficulty];
	const showing = state.faceUp[0];

	if (showing !== undefined) {
		const face = state.cards[showing]?.emoji;
		const partner = moves.find((move) => state.cards[move.card]?.emoji === face);
		if (remembers && partner) return partner;
		return pick(
			moves.filter((move) => move.card !== showing),
			rng,
		);
	}

	if (remembers) {
		// Open with a card whose twin is also still face down: a real pair to win.
		const pairable = moves.filter((move) =>
			moves.some(
				(other) =>
					other.card !== move.card &&
					state.cards[other.card]?.emoji === state.cards[move.card]?.emoji,
			),
		);
		if (pairable.length > 0) return pick(pairable, rng);
	}
	return pick(moves, rng);
}

// --- Dots and boxes: close what you can, then avoid gift-wrapping a box. ---

function dotsBoxesBot(
	state: DotsBoxesState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): DotsBoxesMove | null {
	const moves = dotsBoxes.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	// 1. Anything that claims a box, take it.
	const claiming = moves.filter((move) => boxesClaimedBy(state, move) > 0);
	if (claiming.length > 0) {
		return claiming.reduce((best, move) =>
			boxesClaimedBy(state, move) > boxesClaimedBy(state, best) ? move : best,
		);
	}

	// 2. Otherwise play a line that doesn't hand the other player a box.
	const scored = moves.map((move) => ({ move, gift: boxesOfferedBy(state, move) }));
	const safe = scored.filter((entry) => entry.gift === 0);
	if (safe.length > 0) return pick(safe, rng)?.move ?? null;

	// 3. Cornered: give away as little as possible.
	const cheapest = scored.reduce((best, entry) => (entry.gift < best.gift ? entry : best));
	return cheapest.move;
}

function sidesOfBox(state: DotsBoxesState, box: number): (0 | 1 | null)[] {
	const row = Math.floor(box / DB_BOXES);
	const col = box % DB_BOXES;
	return [
		state.h[row * DB_BOXES + col],
		state.h[(row + 1) * DB_BOXES + col],
		state.v[row * DB_DOTS + col],
		state.v[row * DB_DOTS + col + 1],
	];
}

function boxesTouchedBy(move: DotsBoxesMove): number[] {
	if (move.edge === "h") {
		const row = Math.floor(move.index / DB_BOXES);
		const col = move.index % DB_BOXES;
		const boxes: number[] = [];
		if (row > 0) boxes.push((row - 1) * DB_BOXES + col);
		if (row < DB_BOXES) boxes.push(row * DB_BOXES + col);
		return boxes;
	}
	const row = Math.floor(move.index / DB_DOTS);
	const col = move.index % DB_DOTS;
	const boxes: number[] = [];
	if (col > 0) boxes.push(row * DB_BOXES + col - 1);
	if (col < DB_BOXES) boxes.push(row * DB_BOXES + col);
	return boxes;
}

/** Boxes this line would complete straight away. */
function boxesClaimedBy(state: DotsBoxesState, move: DotsBoxesMove): number {
	return boxesTouchedBy(move).filter(
		(box) =>
			state.boxes[box] === null && sidesOfBox(state, box).filter((s) => s !== null).length === 3,
	).length;
}

/** Boxes this line would leave on three sides, ready for the opponent. */
function boxesOfferedBy(state: DotsBoxesState, move: DotsBoxesMove): number {
	return boxesTouchedBy(move).filter(
		(box) =>
			state.boxes[box] === null && sidesOfBox(state, box).filter((s) => s !== null).length === 2,
	).length;
}

// --- Quiz: knows every answer, so it has to be told how often to be right. ---

const QUIZ_ACCURACY: Record<Difficulty, number> = { easy: 0.4, medium: 0.7, hard: 0.92 };

function quizBot(
	state: QuizState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): QuizMove | null {
	if (state.result || state.phase !== "question" || state.answers[seat]) return null;
	const question = state.questions[state.index];
	if (!question) return null;

	if (rng.next() < QUIZ_ACCURACY[difficulty]) return { choice: question.answer };
	const wrong = question.choices
		.map((_, choice) => choice)
		.filter((choice) => choice !== question.answer);
	return { choice: wrong[rng.int(wrong.length)] ?? question.answer };
}

// --- Nim: solved, so "hard" simply never gives you a way back in. ---

function nimBot(
	state: NimState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): NimMove | null {
	const moves = nim.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);
	const best = nimBestTake(state.sticks);
	// No winning move exists from here — take one and hope they slip.
	return best === null ? (pick(moves, rng) ?? { take: 1 }) : { take: best };
}

// --- Five in a row: win, block, then build near the action. ---

const FIR_WINDOW = 5;
const FIR_DIRECTIONS: readonly (readonly [number, number])[] = [
	[1, 0],
	[0, 1],
	[1, 1],
	[1, -1],
];

function fiveInRowBot(
	state: FiveInRowState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): FiveInRowMove | null {
	const moves = fiveInRow.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	const them = otherPlayer(seat);
	// Only consider squares near existing stones: the rest is empty space.
	const candidates = moves.filter((move) => hasNeighbour(state.board, move.square));
	const search = candidates.length > 0 ? candidates : moves;

	let best: FiveInRowMove | null = null;
	let bestScore = Number.NEGATIVE_INFINITY;
	for (const move of search) {
		const mine = runScore(state.board, move.square, seat);
		const theirs = runScore(state.board, move.square, them);
		// Taking a win beats blocking one; blocking beats anything else.
		const score = mine * 1.1 + theirs;
		if (score > bestScore) {
			bestScore = score;
			best = move;
		}
	}
	return best ?? pick(search, rng);
}

function hasNeighbour(board: (PlayerIndex | null)[], square: number): boolean {
	const col = square % FIR_SIZE;
	const row = Math.floor(square / FIR_SIZE);
	for (let dc = -2; dc <= 2; dc++) {
		for (let dr = -2; dr <= 2; dr++) {
			const c = col + dc;
			const r = row + dr;
			if (c < 0 || c >= FIR_SIZE || r < 0 || r >= FIR_SIZE) continue;
			if (board[r * FIR_SIZE + c] !== null) return true;
		}
	}
	return false;
}

/** How long a run this square would make for `player`, weighted steeply. */
function runScore(board: (PlayerIndex | null)[], square: number, player: PlayerIndex): number {
	const col = square % FIR_SIZE;
	const row = Math.floor(square / FIR_SIZE);
	let total = 0;

	for (const [dc, dr] of FIR_DIRECTIONS) {
		let run = 1;
		let open = 0;
		for (const sign of [1, -1]) {
			let c = col + dc * sign;
			let r = row + dr * sign;
			while (
				c >= 0 &&
				c < FIR_SIZE &&
				r >= 0 &&
				r < FIR_SIZE &&
				board[r * FIR_SIZE + c] === player
			) {
				run++;
				c += dc * sign;
				r += dr * sign;
			}
			if (c >= 0 && c < FIR_SIZE && r >= 0 && r < FIR_SIZE && board[r * FIR_SIZE + c] === null) {
				open++;
			}
		}
		if (run >= FIR_WINDOW) return 1_000_000;
		total += run ** 4 * (open + 1);
	}
	return total;
}

// --- Rock paper scissors: no information, so no strategy to have. ---

function rpsBot(state: RpsState, seat: PlayerIndex, rng: Rng): RpsMove | null {
	const moves = rockPaperScissors.legalMoves(state, seat);
	// Anything other than a uniform pick would be a pattern to exploit.
	return pick(moves, rng);
}

// --- Reflex: the bot has a reaction time, and sometimes jumps the light. ---

/**
 * How long after green the computer taps. This is the whole difficulty knob:
 * the tap itself carries no timing, so it only means anything if the *schedule*
 * (see `botActAt`) anchors on the moment the light turns green rather than on
 * whenever the room happened to ask.
 */
const REFLEX_MS: Record<Difficulty, number> = { easy: 620, medium: 420, hard: 260 };
/** Spread on top of the reaction, so no difficulty taps on a fixed millisecond. */
const REFLEX_JITTER_MS = 160;
/**
 * How often an easy bot gets twitchy and taps before green — a false start,
 * which hands the round over. The room asks the policy twice, once to schedule
 * and once to play, each with a fresh rng, so the rate a child actually sees
 * is roughly the square of this.
 */
const REFLEX_JUMP_CHANCE = 0.25;

function reflexBot(
	state: ReflexState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): ReflexMove | null {
	if (reflexTap.legalMoves(state, seat).length === 0) return null;
	// While the light is still red the only tap worth offering is a jumped
	// one, and only the easy bot is jumpy enough to do it.
	if (state.phase === "wait") {
		return difficulty === "easy" && rng.next() < REFLEX_JUMP_CHANCE ? { tap: true } : null;
	}
	if (state.phase !== "go") return null;
	// Green: always tap. *When* the tap lands is `botActAt`'s job.
	return { tap: true };
}

/**
 * When the computer's reflex tap lands. Measured from green via `REFLEX_MS`,
 * not from when the room asked — the generic think delay put every difficulty
 * at the same 500–1400ms and left the reaction table doing nothing.
 */
function reflexTapAt(state: ReflexState, difficulty: Difficulty, rng: Rng, now: number): number {
	// Red light: the policy has already decided to jump it, so land just short
	// of green — `endsAt` says when green arrives, and the server's bot reads
	// the unredacted state.
	if (state.phase === "wait") return state.endsAt - 60 - rng.int(240);
	const green = state.goAt ?? now;
	return green + REFLEX_MS[difficulty] + rng.int(REFLEX_JITTER_MS);
}

// --- Cards: one has no decisions at all, the other is pure nerve. ---

function warBot(state: WarState, seat: PlayerIndex): WarMove | null {
	// There is nothing to think about in War — you flip, or you don't.
	return warOfCards.legalMoves(state, seat).length > 0 ? { flip: true } : null;
}

/** How often the bot snaps at nothing, which is the only way to lose points. */
const SNAP_MISTAKE: Record<Difficulty, number> = { easy: 0.12, medium: 0.04, hard: 0 };

function snapBot(
	state: SnapState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): SnapMove | null {
	if (speedSnap.legalMoves(state, seat).length === 0) return null;
	if (state.pairUp) return maybeSharp(difficulty, rng) ? { snap: true } : null;
	// Occasionally jumps at a pair that isn't there, like a real player.
	return rng.next() < SNAP_MISTAKE[difficulty] ? { snap: true } : null;
}

// --- Number guess: binary search is optimal, so easy simply doesn't. ---

function numberGuessBot(
	state: NumberGuessState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): NumberGuessMove | null {
	const moves = numberGuess.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	// The endgame is not a solo puzzle: a wrong midpoint hands the other
	// player a halved interval, and from four numbers or fewer that halving
	// wins them the game. Guess an endpoint instead — wrong, it shaves one
	// number off and gives almost nothing away.
	const size = state.high - state.low + 1;
	if (size <= 4) {
		const edge = rng.next() < 0.5 ? state.low : state.high;
		return moves.find((move) => move.guess === edge) ?? pick(moves, rng);
	}
	// Halve the range every time and it never takes more than seven guesses.
	const middle = Math.floor((state.low + state.high) / 2);
	return moves.find((move) => move.guess === middle) ?? pick(moves, rng);
}

// --- Mosaic: the picture is on screen, so this is care, not cunning. ---

function mosaicBot(
	state: MosaicState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): MosaicMove | null {
	const moves = emojiMosaic.legalMoves(state, seat);
	if (moves.length === 0) return null;

	const empty = [...new Set(moves.map((move) => move.square))];
	const square = empty[rng.int(empty.length)];
	if (square === undefined) return null;

	if (maybeSharp(difficulty, rng)) return { square, tile: state.target[square] };
	// A careless copy: right square, wrong colour.
	return { square, tile: rng.int(MOSAIC_TILES.length) };
}

// --- Fleet Battle: finish a wounded ship before hunting for a new one. ---

function fleetBot(
	state: FleetState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): FleetMove | null {
	const moves = fleetBattle.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	const open = new Set(moves.map((move) => move.cell));
	// A ship only counts as sunk once every one of its cells has been hit —
	// taking *all* enemy ship cells here made every hit look "already sunk",
	// which quietly turned the follow-up below into dead code.
	const landed = new Set(state.hits[seat]);
	const sunkCells = new Set(
		state.ships[seat === 0 ? 1 : 0].filter((ship) => ship.every((cell) => landed.has(cell))).flat(),
	);
	// Hits that belong to a ship still afloat are worth following up.
	const wounded = state.hits[seat].filter((cell) => !sunkCells.has(cell));

	for (const cell of wounded) {
		const col = cell % FLEET_SIZE;
		const row = Math.floor(cell / FLEET_SIZE);
		const around = [
			col > 0 ? cell - 1 : -1,
			col < FLEET_SIZE - 1 ? cell + 1 : -1,
			row > 0 ? cell - FLEET_SIZE : -1,
			row < FLEET_SIZE - 1 ? cell + FLEET_SIZE : -1,
		].filter((next) => next >= 0 && open.has(next));
		if (around.length > 0) return { cell: around[rng.int(around.length)] };
	}

	// Nothing wounded: shoot on a checkerboard, which cannot miss a 2-cell ship.
	const parity = moves.filter((move) => (move.cell + Math.floor(move.cell / FLEET_SIZE)) % 2 === 0);
	return pick(parity.length > 0 ? parity : moves, rng);
}

// --- Flip Four: corners are gold, edges next to them are poison. ---

const FLIP_WEIGHTS = [
	[120, -20, 20, 5, 5, 20, -20, 120],
	[-20, -40, -5, -5, -5, -5, -40, -20],
	[20, -5, 15, 3, 3, 15, -5, 20],
	[5, -5, 3, 3, 3, 3, -5, 5],
	[5, -5, 3, 3, 3, 3, -5, 5],
	[20, -5, 15, 3, 3, 15, -5, 20],
	[-20, -40, -5, -5, -5, -5, -40, -20],
	[120, -20, 20, 5, 5, 20, -20, 120],
];

function flipFourBot(
	state: FlipFourState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): FlipFourMove | null {
	const moves = flipFour.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	// Equal-scoring squares are split by the dice, so two hard bots vary from
	// seed to seed instead of replaying one game for ever.
	let bestMoves: FlipFourMove[] = [];
	let bestScore = Number.NEGATIVE_INFINITY;
	for (const move of moves) {
		const row = Math.floor(move.square / 8);
		const col = move.square % 8;
		// Position matters far more than how many discs flip early on.
		const score = FLIP_WEIGHTS[row][col] + flipsFor(state.board, move.square, seat).length;
		if (score > bestScore) {
			bestScore = score;
			bestMoves = [move];
		} else if (score === bestScore) {
			bestMoves.push(move);
		}
	}
	return pick(bestMoves, rng) ?? pick(moves, rng);
}

// --- Code Breaker: guess something that could still be the answer. ---

function codeBreakerBot(
	state: CodeBreakerState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): CodeBreakerMove | null {
	if (state.result || state.turn !== seat) return null;

	// A blind draw can land on a code already on the board, which `apply`
	// rejects — an illegal move, not merely a weak one. Redraw a handful of
	// times, then fall back to the full legal list so the turn cannot stall
	// even on a board crowded with guesses.
	const tried = new Set(state.guesses.map((guess) => guess.code.join(",")));
	const randomCode = (): CodeBreakerMove | null => {
		for (let attempt = 0; attempt < 20; attempt++) {
			const code = Array.from({ length: CODE_LENGTH }, () => rng.int(CODE_COLOURS.length));
			if (!tried.has(code.join(","))) return { code };
		}
		return pick(codeBreaker.legalMoves(state, seat), rng);
	};
	if (!maybeSharp(difficulty, rng)) return randomCode();

	// Only ever guess a code that fits every clue on the board so far. A
	// consistent code can never be a repeat: a repeat's own clue would have
	// had to score four exact pegs, and that guess ends the game.
	const candidates = consistentCodes(state.guesses);
	if (candidates.length === 0) return randomCode();
	return { code: candidates[rng.int(candidates.length)] };
}

// --- Sliding puzzle: a real solver, because greedy gets stuck. ---

const SLIDE_SIZE = 3;

/** How far every tile is from home. Never overestimates, so A* stays optimal. */
function manhattan(board: number[]): number {
	let total = 0;
	for (let index = 0; index < board.length; index++) {
		const tile = board[index];
		if (tile === 0) continue;
		const goal = tile - 1;
		total +=
			Math.abs((index % SLIDE_SIZE) - (goal % SLIDE_SIZE)) +
			Math.abs(Math.floor(index / SLIDE_SIZE) - Math.floor(goal / SLIDE_SIZE));
	}
	return total;
}

function slideNeighbours(blank: number): number[] {
	const col = blank % SLIDE_SIZE;
	const row = Math.floor(blank / SLIDE_SIZE);
	const list: number[] = [];
	if (col > 0) list.push(blank - 1);
	if (col < SLIDE_SIZE - 1) list.push(blank + 1);
	if (row > 0) list.push(blank - SLIDE_SIZE);
	if (row < SLIDE_SIZE - 1) list.push(blank + SLIDE_SIZE);
	return list;
}

/**
 * A* to the solved board, returning only the first tile to slide. A 3×3 board
 * settles in a few hundred nodes, so this is cheap enough to redo every move
 * and means the bot actually finishes rather than shuffling forever.
 */
function solveNextSlide(board: number[]): number | null {
	const goal = board.map((_, index) => (index === board.length - 1 ? 0 : index + 1)).join(",");
	const start = board.join(",");
	if (start === goal) return null;

	const open: { board: number[]; cost: number; first: number | null }[] = [
		{ board, cost: 0, first: null },
	];
	const seen = new Map<string, number>([[start, 0]]);

	for (let steps = 0; steps < 20_000 && open.length > 0; steps++) {
		// Cheapest estimated total first.
		let bestIndex = 0;
		let bestScore = Number.POSITIVE_INFINITY;
		for (let i = 0; i < open.length; i++) {
			const score = open[i].cost + manhattan(open[i].board);
			if (score < bestScore) {
				bestScore = score;
				bestIndex = i;
			}
		}
		const node = open.splice(bestIndex, 1)[0];
		if (node.board.join(",") === goal) return node.first;

		const blank = node.board.indexOf(0);
		for (const tile of slideNeighbours(blank)) {
			const next = [...node.board];
			next[blank] = next[tile];
			next[tile] = 0;
			const key = next.join(",");
			const cost = node.cost + 1;
			if ((seen.get(key) ?? Number.POSITIVE_INFINITY) <= cost) continue;
			seen.set(key, cost);
			open.push({ board: next, cost, first: node.first ?? tile });
		}
	}
	return null;
}

function slidingBot(
	state: SlidingState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): SlidingMove | null {
	const moves = slidingRace.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	const tile = solveNextSlide(state.boards[seat]);
	return tile === null ? pick(moves, rng) : { tile };
}

// --- Pit Row: take another turn if you can, then capture, then sow far. ---

function pitRowBot(
	state: PitRowState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): PitRowMove | null {
	const moves = pitRow.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	let best: PitRowMove | null = null;
	let bestScore = Number.NEGATIVE_INFINITY;
	for (const move of moves) {
		const outcome = pitRow.apply(state, seat, move, 0);
		if (!outcome.ok) continue;
		const gained = outcome.state.scores[seat] - state.scores[seat];
		// Landing in your own store keeps the turn, which is worth a lot.
		const goAgain = outcome.state.turn === seat ? 4 : 0;
		const score = gained * 2 + goAgain;
		if (score > bestScore) {
			bestScore = score;
			best = move;
		}
	}
	return best ?? pick(moves, rng);
}

// --- Checkers: take the capture, promote, and keep off the back rank. ---

function checkersBot(
	state: CheckersState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): CheckersMove | null {
	const moves = checkers.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	let best: CheckersMove | null = null;
	let bestScore = Number.NEGATIVE_INFINITY;
	for (const move of moves) {
		const outcome = checkers.apply(state, seat, move, 0);
		if (!outcome.ok) continue;

		const them = otherPlayer(seat);
		const taken = state.scores[them] - outcome.state.scores[them];
		// A chain that keeps the turn is worth staying on.
		const chain = outcome.state.turn === seat ? 3 : 0;
		const crowned = outcome.state.board[move.to]?.king && !state.board[move.from]?.king ? 4 : 0;
		// Handing them a capture straight back is the classic beginner's mistake.
		const theirBest = checkers
			.legalMoves(outcome.state, them)
			.reduce((worst, reply) => Math.max(worst, captureCount(outcome.state, reply)), 0);

		const score = taken * 6 + chain + crowned - theirBest * 5 + rng.next();
		if (score > bestScore) {
			bestScore = score;
			best = move;
		}
	}
	return best ?? pick(moves, rng);
}

// --- Go Fish: ask at random — the "clever" ask is a trap. ---

function goFishBot(
	state: GoFishState,
	seat: PlayerIndex,
	_difficulty: Difficulty,
	rng: Rng,
): GoFishMove | null {
	const moves = goFish.legalMoves(state, seat);
	if (moves.length === 0) return null;

	// "Ask for what you hold most of" reads like card sense and is exactly
	// backwards: holding three of a rank means only one is left anywhere, so
	// the most-held ask is the one the opponent is *least* likely to answer —
	// and with no memory of past asks it repeats the same failed question for
	// ever. The playtest's policy lab tried every informed ask it could think
	// of and uniform random beat them all, so that is what every difficulty
	// plays; the game's difficulty knob does nothing here on purpose.
	return pick(moves, rng);
}

// --- Old Maid: picking blind is picking blind. ---

function oldMaidBot(state: OldMaidState, seat: PlayerIndex, rng: Rng): OldMaidMove | null {
	// There is genuinely nothing to know here, at any difficulty.
	return pick(oldMaid.legalMoves(state, seat), rng);
}

// --- Minesweeper: deduce from what is showing, never from the mine list. ---

function minesweeperBot(
	state: MinesweeperState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): MinesweeperMove | null {
	const moves = minesweeperDuel.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	const open = new Set(state.revealed);
	const boom = new Set(state.exploded.map((entry) => entry.cell));
	const unknown = (cell: number) => !open.has(cell) && !boom.has(cell);

	const neighboursOf = (cell: number) => {
		const col = cell % MINE_SIZE;
		const row = Math.floor(cell / MINE_SIZE);
		const list: number[] = [];
		for (let dc = -1; dc <= 1; dc++) {
			for (let dr = -1; dr <= 1; dr++) {
				if (dc === 0 && dr === 0) continue;
				const c = col + dc;
				const r = row + dr;
				if (c < 0 || c >= MINE_SIZE || r < 0 || r >= MINE_SIZE) continue;
				list.push(r * MINE_SIZE + c);
			}
		}
		return list;
	};

	// The stored counts include mines that have already gone off, but those are
	// not in the hidden set any more — comparing the raw count against hidden
	// cells marked safe squares as mines, and could then "prove" a live mine
	// safe. Only the mines still waiting to be found may enter the arithmetic.
	const stillBuried = (cell: number) =>
		state.neighbours[cell] - neighboursOf(cell).filter((next) => boom.has(next)).length;

	// A revealed number whose mines are all accounted for makes the rest safe.
	const known = new Set<number>();
	for (const cell of state.revealed) {
		const hidden = neighboursOf(cell).filter(unknown);
		if (hidden.length > 0 && hidden.length === stillBuried(cell)) {
			for (const mine of hidden) known.add(mine);
		}
	}
	const safe: number[] = [];
	for (const cell of state.revealed) {
		const hidden = neighboursOf(cell).filter(unknown);
		const flagged = hidden.filter((next) => known.has(next)).length;
		if (flagged === stillBuried(cell)) {
			for (const next of hidden) if (!known.has(next)) safe.push(next);
		}
	}

	if (safe.length > 0) return { cell: safe[rng.int(safe.length)] };
	// Nothing provable: take the least-bad guess, never a known mine.
	const risky = moves.filter((move) => !known.has(move.cell));
	return pick(risky.length > 0 ? risky : moves, rng);
}

// --- Last Card: dump big numbers, hold the specials for when they matter. ---

function lastCardBot(
	state: LastCardState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): LastCardMove | null {
	const moves = lastCard.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	const hand = state.hands[seat];
	const plays = moves.filter((move): move is { play: number } => "play" in move);
	if (plays.length === 0) return moves[0];

	/*
	 * Two lessons from the policy lab, both against intuition. First, hoarding
	 * specials for the endgame loses: ⛔ and +2 keep *your* turn, so playing
	 * one sheds a card and immediately plays another — a chain, not a spent
	 * shell — and a special in hand answers a threat exactly as well played
	 * now as later. Second, "dump the biggest number" is noise dressed as
	 * strategy: values score nothing here, but every play changes the top
	 * card, so the number worth playing is the one the rest of the hand can
	 * follow — most matches by colour or value among the cards staying behind.
	 */
	const specials = plays.filter((move) => (hand[move.play]?.value ?? 0) >= SKIP);
	if (specials.length > 0) {
		// A +2 sets them back as well as skipping them; take it when offered.
		return (
			specials.find((move) => hand[move.play]?.value === DRAW_TWO) ??
			specials[rng.int(specials.length)]
		);
	}
	const followups = (index: number) => {
		const card = hand[index];
		if (!card) return 0;
		return hand.filter(
			(other, i) => i !== index && (other.colour === card.colour || other.value === card.value),
		).length;
	};
	return plays.reduce((best, move) => (followups(move.play) > followups(best.play) ? move : best));
}

// --- Connect-the-dots: it is a race, so it is all reaction time. ---

function dotsRaceBot(
	state: DotsRaceState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): DotsRaceMove | null {
	const moves = dotsRace.legalMoves(state, seat);
	if (moves.length === 0) return null;
	// Sharpness stands in for how quickly the bot spots the next number.
	return maybeSharp(difficulty, rng) ? moves[0] : null;
}

// --- Typing Ladder: knows the word, told how often to find the letter. ---

function typingLadderBot(
	state: TypingLadderState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): TypingLadderMove | null {
	const moves = typingLadder.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	// The next letter it needs, if it can spot the tile.
	const wanted = state.word[state.progress[seat]];
	const tile = state.tiles.indexOf(wanted);
	return tile === -1 ? pick(moves, rng) : { tile };
}

// --- Emoji Charades: acts at random, guesses by difficulty. ---

function charadesBot(
	state: CharadesState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): CharadesMove | null {
	if (state.result) return null;

	if (state.phase === "acting") {
		if (seat !== state.actor) return null;
		// No language model in a Durable Object: the bot mimes at random, which
		// makes it a cheerfully useless partner and an honest one.
		return { clue: rng.int(CHARADE_EMOJI.length) };
	}

	if (state.phase === "guessing" && seat !== state.actor) {
		const options = emojiCharades.legalMoves(state, seat);
		if (options.length === 0) return null;
		// It can see the answer, so difficulty is how often it uses that.
		if (rng.next() < QUIZ_ACCURACY[difficulty]) return { guess: state.answer };
		const wrong = state.options.map((_, index) => index).filter((index) => index !== state.answer);
		return { guess: wrong[rng.int(wrong.length)] ?? state.answer };
	}
	return null;
}

// --- Suit Run: open in the middle, take what leads on, swap what doesn't. ---

function suitRunBot(
	state: SuitRunState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): SuitRunMove | null {
	const moves = suitRun.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	const run = state.runs[seat];
	const takes = moves.filter((move): move is { take: number } => "take" in move);

	// Open on a mid-value. "Start as low as possible" was right when runs only
	// grew upward; now they grow both ways, a 4–6 keeps both ends alive where
	// an ace throws the whole downward half of the suit away.
	if (run.length === 0 && takes.length > 0) {
		return takes.reduce((best, move) => {
			const value = state.market[move.take]?.value ?? 99;
			const bestValue = state.market[best.take]?.value ?? 99;
			return Math.abs(value - 5) < Math.abs(bestValue - 5) ? move : best;
		});
	}

	// How many cards could still carry the run on after this take — the deck
	// and the market are what is left of the world, so a take that leaves the
	// new ends reachable is worth more than the bare point it scores.
	const available = [...state.deck, ...state.market].filter(
		(card): card is NonNullable<typeof card> => card !== null,
	);
	const prospectsAfter = (move: { take: number }): number => {
		const card = state.market[move.take];
		if (!card) return 0;
		let low = card.value;
		let high = card.value;
		for (const laid of run) {
			low = Math.min(low, laid.value);
			high = Math.max(high, laid.value);
		}
		return available.filter(
			(next) =>
				next !== card &&
				next.suit === card.suit &&
				(next.value === low - 1 || next.value === high + 1),
		).length;
	};

	if (takes.length > 0) {
		const best = takes.reduce((a, b) => (prospectsAfter(b) > prospectsAfter(a) ? b : a));
		// A take that keeps the run alive beats everything else on offer.
		if (prospectsAfter(best) > 0) return best;

		// Every take is a dead end. Before banking the last point, spend the
		// turn killing the card the other run is waiting for — the dead-end
		// take is still there next turn, their extension will not be.
		const them = otherPlayer(seat);
		const deny = moves.find((move) => {
			if (!("swap" in move)) return false;
			const card = state.market[move.swap];
			return (
				card !== null && card !== undefined && fits(state.runs[them], card) && !fits(run, card)
			);
		});
		return deny ?? best;
	}
	return pick(moves, rng);
}

// --- Word Ladder: step towards the target, less reliably when it is easy. ---

function wordLadderBot(
	state: WordLadderState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): WordLadderMove | null {
	const moves = wordLadder.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);
	return bestLadderStep(state, seat) ?? pick(moves, rng);
}

// --- Snail Dash: tap near the beat, with a human-sized wobble. ---

/**
 * How far off the beat the computer lands, in milliseconds either way.
 *
 * These have to be read against the hit window, not chosen for feel: a wobble
 * smaller than the window means the snail is *incapable* of missing, which is
 * what the first playtest showed. Spread uniformly, the chance of a hit is
 * window ÷ wobble — so these come out at roughly 45%, 70% and 90%.
 */
const DASH_WOBBLE: Record<Difficulty, number> = { easy: 580, medium: 370, hard: 290 };
/** Keeps scheduled taps clear of the window's edges, where jitter flips them. */
const DASH_EDGE_MS = 40;

/**
 * Whether the snail makes this beat, decided once per beat.
 *
 * Two earlier designs died here. Placing a "missed" tap late fed it to the
 * *next* beat's judging; placing it early landed in the drum-roll no-op zone,
 * which spent nothing — so any time the room asked again, the bot re-rolled
 * until it hit, and every difficulty converged on a perfect metronome. A miss
 * has to consume the beat without a tap existing at all, and the roll has to
 * come from the *state* rather than a fresh generator, so however many times
 * the room re-polls mid-beat it always gets the same answer. The state's
 * `rollSeed` is dealt per game, so the pattern still differs from race to
 * race — and per seat, so two computer snails miss on different beats.
 */
function snailBeatHits(state: SnailDashState, seat: PlayerIndex, difficulty: Difficulty): boolean {
	const roll = createRng((state.rollSeed + state.beatNumber * 9_973 + seat * 31_337) >>> 0).next();
	return roll < DASH_WINDOW_MS / DASH_WOBBLE[difficulty];
}

/**
 * The policy side of the miss: on a beat rolled as a miss there is no tap to
 * offer, so the room schedules nothing, the beat closes over the silent snail,
 * and `tick` charges the miss — exactly what a player who fluffed the rhythm
 * looks like from outside.
 */
function snailDashBot(
	state: SnailDashState,
	seat: PlayerIndex,
	difficulty: Difficulty,
): SnailDashMove | null {
	if (state.result || state.tapped[seat]) return null;
	return snailBeatHits(state, seat, difficulty) ? { tap: true } : null;
}

/**
 * When a hit tap lands: safely inside the window, clear of the edges where
 * jitter would flip it. Only ever consulted for beats the policy tapped on —
 * `snailBeatHits` owns hit-or-miss, this only owns *where in the window*.
 */
function snailDashTapAt(state: SnailDashState, rng: Rng): number {
	const spread = DASH_WINDOW_MS - DASH_EDGE_MS;
	return state.beatAt + Math.round((rng.next() * 2 - 1) * spread);
}

/**
 * When the computer should play its next move.
 *
 * Almost every game just wants a pause long enough to look like thinking. The
 * reaction games do not: tapping a fixed moment after being asked would miss
 * every beat however good the snail was meant to be, and would give Reflex the
 * same reaction at every difficulty — so those aim at the beat and the green
 * light instead. Kept here with the rest of the bot policy, where it is a pure
 * function of the state and can be tested, rather than as a special case
 * inside the room.
 */
export function botActAt(state: GameState, difficulty: Difficulty, rng: Rng, now: number): number {
	if (state.kind === "snaildash") {
		return Math.max(now, snailDashTapAt(state, rng));
	}
	if (state.kind === "reflex") {
		return Math.max(now, reflexTapAt(state, difficulty, rng, now));
	}
	return now + botThinkDelay(difficulty, rng);
}

// --- Snakes and Ladders: take the number that leaves you further up. ---

function snakesLaddersBot(
	state: SnakesLaddersState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): SnakesLaddersMove | null {
	const moves = snakesLadders.legalMoves(state, seat);
	if (moves.length === 0) return null;
	// Rolling is not a decision, so there is nothing to be bad at yet.
	if (moves.length === 1) return moves[0];
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);
	return bestLadderChoice(state, seat) ?? pick(moves, rng);
}

// --- Turbo Track: press on down the straights, lift before the bends. ---

/** How often a level bot takes the bend in second gear anyway. */
const TURBO_RISK = 0.15;

function turboTrackBot(
	state: TurboTrackState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): TurboTrackMove | null {
	const moves = turboTrack.legalMoves(state, seat);
	if (moves.length === 0) return null;
	// An easy opponent drives it like a child would: flat out, everywhere.
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	const careful = bestGear(state, seat) ?? pick(moves, rng);

	/*
	 * `bestGear` alone is deterministic and never risks a bend, and two of it
	 * drive the same race to a mirror draw every single time. A real driver
	 * gambles: trailing, there is nothing to lose — and even level, sometimes
	 * you send it. Second gear through a bend has positive expected ground
	 * (two-in-six spin against four extra squares), just more variance than
	 * the careful line, which is exactly what a race needs to be decided.
	 */
	const them = otherPlayer(seat);
	const trailing = state.at[seat] < state.at[them];
	if (careful?.gear === 1 && (trailing || rng.next() < TURBO_RISK)) {
		const quick = GEARS[1];
		const target = Math.min(TRACK_LENGTH, state.at[seat] + quick.moves);
		const bends = cornersCrossed(state.at[seat], target);
		const safe = (1 - quick.spinInSix / 6) ** bends;
		const gained = target - state.at[seat];
		if (bends > 0 && safe * gained - (1 - safe) * gained > 0) return { gear: quick.gear };
	}
	return careful;
}

// --- Peg Chase: the die is the die; which peg you move is the game. ---

function pegChaseBot(
	state: PegChaseState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): PegChaseMove | null {
	const moves = pegChase.legalMoves(state, seat);
	if (moves.length === 0) return null;
	// Rolling is not a decision, so there is nothing to be bad at yet.
	if (moves.length === 1) return moves[0];
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);
	return bestPegMove(state, seat) ?? pick(moves, rng);
}

// --- Rummy Rush: keep what is nearly a group, throw the rest. ---

function rummyBot(
	state: RummyState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): RummyMove | null {
	const moves = rummyRush.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);
	return bestRummyMove(state, seat) ?? pick(moves, rng);
}

// --- Logic Grid: deduce, or guess wildly when it is meant to be easy. ---

function logicGridBot(
	state: LogicGridState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): LogicGridMove | null {
	const moves = logicGrid.legalMoves(state, seat);
	if (moves.length === 0) return null;
	// An easy opponent guesses from what the clues allow without carrying the
	// earlier scores forward — which is exactly the mistake a child makes.
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);
	return deduceGrid(state, rng) ?? pick(moves, rng);
}

// --- Route Blocker: walk the short way, fence when it is worth it. ---

function routeBlockerBot(
	state: RouteBlockerState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): RouteBlockerMove | null {
	const moves = routeBlocker.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);
	// Easy never fences: it just races, which is a gentle opponent to learn on.
	return (
		bestBlockerMove(state, seat, { useFences: difficulty !== "easy", rng }) ?? pick(moves, rng)
	);
}

// --- Tower Swap: cap if you can, deny theirs, and actually build. ---

function towerSwapBot(
	state: TowerSwapState,
	seat: PlayerIndex,
	difficulty: Difficulty,
	rng: Rng,
): TowerSwapMove | null {
	const moves = towerSwap.legalMoves(state, seat);
	if (moves.length === 0) return null;
	if (!maybeSharp(difficulty, rng)) return pick(moves, rng);

	const them = otherPlayer(seat);
	let best: TowerSwapMove | null = null;
	let bestScore = Number.NEGATIVE_INFINITY;
	for (const move of moves) {
		const outcome = towerSwap.apply(state, seat, move, 0);
		if (!outcome.ok) continue;
		const claimed = outcome.state.scores[seat] - state.scores[seat];
		// Never hand them a cap: any reply that scores is a finished 6-tower
		// with the 1 free to land on it, and it ends the game against us.
		const theirClaims = towerSwap.legalMoves(outcome.state, them).filter((reply) => {
			const after = towerSwap.apply(outcome.state, them, reply, 0);
			return after.ok && after.state.scores[them] > outcome.state.scores[them];
		}).length;

		/*
		 * The build terms, which the first bot did not have — it valued only
		 * claims and denials, so two of them shuffled blocks until the stall
		 * clock ran out, every single game. A cap needs a six-block tower to
		 * exist first, so growing the tallest still-growable tower is worth
		 * real points, with two cautions: a tower whose top is the 1 cannot
		 * grow until the 1 moves off, and parking the 1 on the build wastes
		 * the only capping block — so both are marked down.
		 */
		const towers = outcome.state.towers;
		const grows = towers.map((tower) =>
			tower.length > 0 && tower[tower.length - 1] === 1 ? tower.length - 1 : tower.length,
		);
		const tallest = Math.max(...grows);
		const tallestAt = grows.indexOf(tallest);
		// The stall settle goes to whoever laid more of the tallest tower, so
		// a lead in placements there is worth keeping even when no cap comes.
		const stallLead = outcome.state.placed[seat][tallestAt] - outcome.state.placed[them][tallestAt];
		const block = state.towers[move.from][state.towers[move.from].length - 1];
		const parkedTheOne = block === 1 && towers[move.to].length <= TOWER_CLAIM_HEIGHT ? 1 : 0;

		// The random term is deliberately big enough to split near-equal moves
		// differently on different seeds — two deterministic hard bots otherwise
		// mirror each other into an even split and a permanent draw.
		const score =
			claimed * 1000 -
			theirClaims * 300 +
			tallest * 8 +
			stallLead * 4 -
			parkedTheOne * 12 +
			rng.next() * 5;
		if (score > bestScore) {
			bestScore = score;
			best = move;
		}
	}
	return best ?? pick(moves, rng);
}
